import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Users, SkipForward, Share2, Copy, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { getClientId, getNick, setNick, getHostKey } from "../lib/party";
import { VideoPlayer } from "../components/VideoPlayer";
import { PartyChat } from "../components/PartyChat";
import { logger } from "../lib/logger";

const POLL_MS = 3000;

export default function PartyRoom() {
  const { code } = useParams();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState("");
  const [state, setState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [viewers, setViewers] = useState(1);
  const [nick, setNickState] = useState(getNick());
  const [nickDraft, setNickDraft] = useState("");
  const clientId = useMemo(() => getClientId(), []);
  const hostKey = getHostKey(code);
  const lastTs = useRef(null);
  const indexRef = useRef(-1);

  useEffect(() => {
    let cancelled = false;
    api.get(`/party/${code}`)
      .then(r => {
        if (cancelled) return;
        indexRef.current = r.data.state.index;
        setRoom(r.data);
        setState(r.data.state);
      })
      .catch(err => { if (!cancelled) setError(apiError(err, "Salle introuvable")); });
    return () => { cancelled = true; };
  }, [code]);

  useEffect(() => {
    if (!room) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const params = { client_id: clientId, nick };
        if (lastTs.current) params.since = lastTs.current;
        const { data } = await api.get(`/party/${code}/sync`, { params });
        if (stop) return;
        setViewers(data.viewers);
        if (data.messages.length) {
          lastTs.current = data.messages[data.messages.length - 1].ts;
          setMessages(prev => {
            const seen = new Set(prev.map(m => m.id));
            return [...prev, ...data.messages.filter(m => !seen.has(m.id))];
          });
        }
        if (data.state.index !== indexRef.current) {
          indexRef.current = data.state.index;
          setState(data.state);
        }
      } catch (err) {
        if (err.response?.status === 404) setError("Cette salle a expiré");
        else logger.warn("[party] sync failed:", err);
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { stop = true; clearInterval(id); };
  }, [room, code, clientId, nick]);

  const send = async (text) => {
    try {
      const { data } = await api.post(`/party/${code}/chat`, { nick, text, client_id: clientId });
      setMessages(prev => (prev.some(m => m.id === data.id) ? prev : [...prev, data]));
    } catch (err) {
      toast.error(apiError(err, "Message non envoyé"));
    }
  };

  const skip = async () => {
    try {
      const { data } = await api.post(`/party/${code}/skip`, { host_key: hostKey });
      indexRef.current = data.state.index;
      setState(data.state);
    } catch (err) {
      toast.error(apiError(err, "Impossible de passer la vidéo"));
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/party/${code}`;
    try {
      if (navigator.share) { await navigator.share({ title: `Watch Party ${code}`, url }); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Lien de la salle copié");
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Impossible de copier");
    }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(code); toast.success(`Code ${code} copié`); } catch { toast.error("Impossible de copier"); }
  };

  const saveNick = (e) => {
    e.preventDefault();
    const n = nickDraft.trim();
    if (!n) return;
    setNick(n);
    setNickState(n);
  };

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="party-error">
        <h1 className="font-display text-4xl">{error}</h1>
        <p className="text-[color:var(--text-muted)] mt-3">Les salles expirent après 24h sans activité.</p>
        <Link to="/party" className="mt-8 inline-block bg-[color:var(--accent)] text-black font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-widest">Créer une nouvelle salle</Link>
      </div>
    );
  }
  if (!room || !state) {
    return <div className="p-24 text-center text-[color:var(--text-muted)]" data-testid="party-loading"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  }

  const ch = room.channel;
  const video = state.video;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-6 grid lg:grid-cols-[1fr_380px] gap-6" data-testid="party-room">
      <div>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="w-3 h-3 rounded-full" style={{ background: ch.color }} />
          <span className="text-xs uppercase tracking-[0.3em] text-[color:var(--text-muted)] inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Watch Party</span>
          <span className="font-display text-3xl">{ch.name}</span>
          <button onClick={copyCode} data-testid="party-room-code" title="Copier le code" className="inline-flex items-center gap-2 font-mono tracking-[0.25em] text-sm px-3 py-1.5 rounded-full border border-[color:var(--accent)]/50 text-[color:var(--accent)] hover:bg-[color:var(--accent)] hover:text-black transition-colors">
            {code} <Copy className="w-3.5 h-3.5" />
          </button>
          <span className="ml-auto flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--accent)]"><span className="live-dot" /> {viewers} en ligne</span>
        </div>
        <div className="crt-tv">
          <span className="crt-tv-label">{ch.name.toUpperCase()}</span>
          <span className="crt-knob l" />
          <span className="crt-knob r" />
          <div className="crt-screen">
            <VideoPlayer key={`${video?.id}-${state.index}`} video={video} autoplay startAt={state.offset} />
          </div>
        </div>
        <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-display text-3xl" data-testid="party-video-title">{video?.title}</h1>
            <p className="text-sm text-[color:var(--text-muted)] mt-1 max-w-2xl">{video?.description || ch.description}</p>
            <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-2" data-testid="party-position">
              Vidéo {state.index + 1} / {room.playlist_size} · Hôte : <span className="text-white inline-flex items-center gap-1"><Crown className="w-3 h-3 text-[color:var(--accent)]" />{room.host_nick}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={share} data-testid="party-share" className="inline-flex items-center gap-2 border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-colors">
              <Share2 className="w-4 h-4" /> Inviter
            </button>
            {hostKey && (
              <button onClick={skip} data-testid="party-skip" className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-4 py-2 rounded-full text-xs uppercase tracking-wider hover:brightness-110 transition">
                <SkipForward className="w-4 h-4" /> Vidéo suivante
              </button>
            )}
          </div>
        </div>
      </div>

      <aside className="relative">
        <PartyChat messages={messages} viewers={viewers} nick={nick} clientId={clientId} onSend={send} disabled={!nick} />
        {!nick && (
          <form onSubmit={saveNick} className="absolute inset-0 z-10 rounded-xl bg-black/70 backdrop-blur-sm grid place-items-center p-6" data-testid="party-nick-gate">
            <div className="w-full max-w-xs space-y-3 text-center">
              <div className="font-display text-2xl">Choisissez un pseudo</div>
              <input data-testid="party-nick-input" value={nickDraft} onChange={e => setNickDraft(e.target.value)} maxLength={24} autoFocus placeholder="ex. B-Girl Nao" className="w-full bg-[color:var(--bg-elev)] border border-[color:var(--border)] rounded-full px-4 py-2.5 text-sm text-center focus:outline-none focus:border-[color:var(--accent)]" />
              <button data-testid="party-nick-submit" disabled={!nickDraft.trim()} className="w-full bg-[color:var(--accent)] text-black font-semibold px-5 py-2.5 rounded-full text-xs uppercase tracking-widest disabled:opacity-50">Rejoindre le chat</button>
            </div>
          </form>
        )}
      </aside>
    </div>
  );
}
